import { useState, type FormEvent } from 'react';
import { useAuthStore } from '../../store/auth-store';
import styles from './PasswordResetConfirm.module.css';

interface Props {
  onDone: () => void;
}

export function PasswordResetConfirm({ onDone }: Props) {
  const updatePassword = useAuthStore((s) => s.updatePassword);
  const loading = useAuthStore((s) => s.loading);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (password.length < 8) {
      setLocalError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setLocalError('Passwords do not match.');
      return;
    }

    const { error } = await updatePassword(password);
    if (error) {
      setLocalError(error.message);
      return;
    }

    setSuccess(true);
  };

  const handleContinue = () => {
    if (window.location.hash) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    onDone();
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        {success ? (
          <>
            <h1 className={styles.title}>Password updated</h1>
            <p className={styles.subtitle}>
              You're signed in with your new password.
            </p>
            <button type="button" onClick={handleContinue} className={styles.submitButton}>
              Continue
            </button>
          </>
        ) : (
          <>
            <h1 className={styles.title}>Set a new password</h1>
            <p className={styles.subtitle}>
              Enter a new password for your Neume account.
            </p>

            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.field}>
                <label htmlFor="new-password">New password</label>
                <input
                  id="new-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                  required
                  disabled={loading}
                />
              </div>

              <div className={styles.field}>
                <label htmlFor="confirm-password">Confirm password</label>
                <input
                  id="confirm-password"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  required
                  disabled={loading}
                />
              </div>

              {localError && <div className={styles.error}>{localError}</div>}

              <button type="submit" className={styles.submitButton} disabled={loading}>
                {loading ? 'Updating…' : 'Update password'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
